import { portalFetch } from '../../shared/auth/portalSession';
import { getAuthHeaders, isAuthenticated, requireAuthentication } from './authUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface AppointmentData {
  appointmentId?: string;
  fullName: string;
  phoneNumber: string;
  idCard: string;
  village: string;
  provider?: string; // כללית, מאוחדת, מכבי, לאומית
  clinicName?: string;
  createdBy?: string;
  serviceType: number; // 1 = Xray, 2 = US
  serviceId?: string;
  serviceName?: string;
  selectedDay: string;
  selectedDayLabel: string;
  selectedTime: string;
  intervalMinutes?: number; // Interval used when booking - preserves original schedule settings
  status: 'draft' | 'completed' | 'closed' | 'cancelled';
  createdAt?: string;
  updatedAt?: string;
  isSynced?: boolean;
  isReserveSlot?: boolean; // Flag to indicate if this is a reserve slot appointment
  confirmationStatus?: string;
  reminderSent?: boolean;
  cancelReason?: string;
}

export interface TimeSlot {
  time: string;
  isAvailable: boolean;
  intervalMinutes?: number; // Interval in minutes for this slot
}

export interface AvailableDate {
  date: string;
  dayName: string;
  availableSlots: number;
  totalSlots: number;
}

export interface GetAppointmentsParams {
  day?: string;
  serviceType?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Create a new appointment or update draft
 */
export const createAppointmentApi = async (appointment: AppointmentData): Promise<AppointmentData> => {
  requireAuthentication();
  
  const response = await portalFetch('admin', `${API_BASE_URL}/appointments`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(appointment),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create appointment');
  }

  return response.json();
};

/**
 * Update existing appointment
 */
export const updateAppointmentApi = async (
  appointmentId: string,
  updates: Partial<AppointmentData>
): Promise<AppointmentData> => {
  requireAuthentication();
  
  const response = await portalFetch('admin', `${API_BASE_URL}/appointments/${appointmentId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update appointment');
  }

  return response.json();
};

/**
 * Get appointments with optional filtering
 */
export const getAppointmentsApi = async (params: GetAppointmentsParams = {}): Promise<AppointmentData[]> => {
  requireAuthentication();
  
  const queryParams = new URLSearchParams();
  
  if (params.day) queryParams.append('day', params.day);
  if (params.serviceType) queryParams.append('serviceType', params.serviceType.toString());
  if (params.status) queryParams.append('status', params.status);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);

  const url = `${API_BASE_URL}/appointments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  const response = await portalFetch('admin', url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get appointments');
  }

  return response.json();
};

/**
 * Get available time slots for a specific date and service type
 */
export const getAvailableSlotsApi = async (
  date: string,  // Changed from 'day' to 'date' (format: YYYY-MM-DD)
  serviceType: number,
  serviceId?: string,
  showAll: boolean = false  // Admin parameter to show all slots (booked + available)
): Promise<TimeSlot[]> => {
  requireAuthentication();
  
  // Convert serviceType to scheduleType (1=Xray, 2=US)
  const scheduleType = serviceType === 1 ? 'Xray' : 'US';
  
  const queryParams = new URLSearchParams({
    date,  // Changed from 'day' to 'date'
    scheduleType,
  });
  
  if (serviceId) {
    queryParams.append('serviceId', serviceId);
  }
  
  if (showAll) {
    queryParams.append('showAll', 'true');
  }

  const response = await portalFetch('admin', `${API_BASE_URL}/available-slots?${queryParams.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get available slots');
  }

  return response.json();
};

/**
 * Get admin schedule with all slots and appointment details (Admin Only)
 */
export interface AdminScheduleSlot {
  time: string;
  isAvailable: boolean;
  capacity: number;
  booked: number;
  intervalMinutes: number;
  technicianId: string;
  technicianName: string;
  appointments: Array<{
    appointmentId: string;
    patientName: string;
    phoneNumber: string;
    idCard?: string;
    provider: string;
    serviceName: string;
    serviceId?: string;
    isSynced: boolean;
    status: string;
    confirmationStatus: string;
    confirmationToken?: string;
    reminderSent: boolean;
    isReserveSlot: boolean;
    createdBy: string;
    clinicName: string;
    village: string;
    createdAt: string;
  }>;
}

export const getAdminScheduleApi = async (
  date: string,
  scheduleType: 'US' | 'Xray',
  signal?: AbortSignal
): Promise<AdminScheduleSlot[]> => {
  requireAuthentication();
  
  const queryParams = new URLSearchParams({
    date,
    scheduleType,
  });

  const response = await portalFetch('admin', `${API_BASE_URL}/admin/schedule?${queryParams.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get admin schedule');
  }

  return response.json();
};

/**
 * Get available dates for the next 2 months, excluding fully booked days
 */
export const getAvailableDatesApi = async (
  serviceType: number,
  serviceId?: string
): Promise<AvailableDate[]> => {
  requireAuthentication();
  
  // Convert serviceType to scheduleType (1=Xray, 2=US)
  const scheduleType = serviceType === 1 ? 'Xray' : 'US';
  
  const queryParams = new URLSearchParams({
    scheduleType,
  });
  
  if (serviceId) {
    queryParams.append('serviceId', serviceId);
  }

  const response = await portalFetch('admin', `${API_BASE_URL}/available-dates?${queryParams.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get available dates');
  }

  return response.json();
};

/**
 * Delete appointment (for drafts or cancellations)
 */
export const deleteAppointmentApi = async (appointmentId: string): Promise<void> => {
  requireAuthentication();
  
  const response = await portalFetch('admin', `${API_BASE_URL}/appointments/${appointmentId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete appointment');
  }
};

/**
 * Update sync status for an appointment
 */
export const updateAppointmentSyncStatusApi = async (
  appointmentId: string,
  isSynced: boolean
): Promise<AppointmentData> => {
  console.log('updateAppointmentSyncStatusApi called:', { appointmentId, isSynced });
  
  if (!isAuthenticated()) {
    console.error('Not authenticated');
    throw new Error('Authentication required');
  }
  
  const url = `${API_BASE_URL}/appointments/${appointmentId}/sync`;
  const headers = getAuthHeaders();
  const body = JSON.stringify({ isSynced });
  
  console.log('Making PATCH request to:', url);
  console.log('Request body:', body);
  
  const response = await portalFetch('admin', url, {
    method: 'PATCH',
    headers,
    body,
  });

  console.log('Response status:', response.status);
  console.log('Response ok:', response.ok);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Error response:', errorText);
    try {
      const error = JSON.parse(errorText);
      throw new Error(error.message || 'Failed to update sync status');
    } catch {
      throw new Error(`Failed to update sync status: ${response.status} ${response.statusText}`);
    }
  }

  const result = await response.json();
  console.log('Success response:', result);
  return result;
};
